import { memo, useCallback } from 'react';
import type { TimelineItem } from '@/types/timeline';
import { ClipFilmstrip } from '../clip-filmstrip';
import { ClipWaveform } from '../clip-waveform';
import {
  CLIP_LABEL_ROW_HEIGHT,
  VIDEO_WAVEFORM_HEIGHT,
} from '@/features/timeline/constants';
import { useSettingsStore } from '@/features/settings/stores/settings-store';
import { useMediaLibraryStore } from '@/features/media-library/stores/media-library-store';
import { useCompositionsStore } from '../../stores/compositions-store';

interface ClipContentProps {
  item: TimelineItem;
  clipWidth: number;
  fps: number;
  isClipVisible: boolean;
  pixelsPerSecond: number;
  preferImmediateRendering?: boolean;
}

function SimpleClipContent({ item }: { item: TimelineItem }) {
  if (item.type === 'text') {
    return (
      <div className="absolute inset-0 flex flex-col px-2 py-1 overflow-hidden">
        <div className="text-[10px] text-muted-foreground truncate">Text</div>
        <div className="text-xs font-medium truncate flex-1">
          {item.text || 'Empty text'}
        </div>
      </div>
    );
  }

  if (item.type === 'adjustment') {
    const enabledEffectsCount = item.effects?.filter((effect) => effect.enabled).length ?? 0;
    return (
      <div className="absolute inset-0 flex flex-col px-2 py-1 overflow-hidden">
        <div className="text-[10px] text-muted-foreground truncate">Adjustment Layer</div>
        <div className="text-xs font-medium truncate flex-1">
          {enabledEffectsCount > 0
            ? `${enabledEffectsCount} effect${enabledEffectsCount > 1 ? 's' : ''}`
            : 'No effects'}
        </div>
      </div>
    );
  }

  const typeLabel = item.type === 'shape' ? 'Shape' : item.type === 'image' ? 'Image' : 'Clip';
  return (
    <div className="absolute inset-0 flex flex-col px-2 py-1 overflow-hidden">
      <div className="text-[10px] text-muted-foreground truncate">{typeLabel}</div>
      <div className="text-xs font-medium truncate flex-1">
        {item.label || typeLabel}
      </div>
    </div>
  );
}

function useMediaSourceTiming(effectiveMediaId: string | null | undefined, item: TimelineItem, fps: number) {
  const sourceFps = useMediaLibraryStore(
    useCallback((s) => {
      if (!effectiveMediaId) return fps;
      const media = s.mediaItems.find((m) => m.id === effectiveMediaId);
      return media?.fps || fps;
    }, [effectiveMediaId, fps])
  );
  const mediaDuration = useMediaLibraryStore(
    useCallback((s) => {
      if (!effectiveMediaId) return 0;
      const media = s.mediaItems.find((m) => m.id === effectiveMediaId);
      return media?.duration || 0;
    }, [effectiveMediaId])
  );

  const sourceDurationFrames = Math.max(1, item.sourceDuration ?? item.durationInFrames);
  const sourceStartFrames = Math.max(0, item.sourceStart ?? 0);

  const sourceDuration = mediaDuration > 0
    ? mediaDuration
    : (sourceDurationFrames / sourceFps);
  const sourceStart = mediaDuration > 0
    ? (sourceStartFrames / sourceDurationFrames) * mediaDuration
    : (sourceStartFrames / sourceFps);

  return { sourceDuration, sourceStart };
}

function VideoClipContent({
  item,
  clipWidth,
  fps,
  isClipVisible,
  pixelsPerSecond,
  preferImmediateRendering = false,
}: ClipContentProps) {
  const showWaveforms = useSettingsStore((s) => s.showWaveforms);
  const showFilmstrips = useSettingsStore((s) => s.showFilmstrips);
  const { sourceDuration, sourceStart } = useMediaSourceTiming(item.mediaId, item, fps);
  const trimStart = (item.trimStart ?? 0) / fps;
  const speed = item.speed ?? 1;

  return (
    <div className="absolute inset-0 flex flex-col">
      <div
        className="px-2 text-[11px] font-medium truncate shrink-0"
        style={{ height: CLIP_LABEL_ROW_HEIGHT, lineHeight: `${CLIP_LABEL_ROW_HEIGHT}px` }}
      >
        {item.label}
      </div>
      <div className="relative overflow-hidden flex-1 min-h-0">
        {showFilmstrips && item.mediaId && (
          <ClipFilmstrip
            mediaId={item.mediaId}
            clipWidth={clipWidth}
            sourceStart={sourceStart}
            sourceDuration={sourceDuration}
            trimStart={trimStart}
            speed={speed}
            fps={fps}
            isVisible={isClipVisible}
            pixelsPerSecond={pixelsPerSecond}
            preferImmediateRendering={preferImmediateRendering}
          />
        )}
      </div>
      {showWaveforms && item.mediaId && (
        <div className="relative overflow-hidden bg-waveform-gradient" style={{ height: VIDEO_WAVEFORM_HEIGHT }}>
          <ClipWaveform
            mediaId={item.mediaId}
            clipWidth={clipWidth}
            sourceStart={sourceStart}
            sourceDuration={sourceDuration}
            trimStart={trimStart}
            speed={speed}
            fps={fps}
            isVisible={isClipVisible}
            pixelsPerSecond={pixelsPerSecond}
          />
        </div>
      )}
    </div>
  );
}

function AudioClipContent({
  item,
  clipWidth,
  fps,
  isClipVisible,
  pixelsPerSecond,
}: ClipContentProps) {
  const showWaveforms = useSettingsStore((s) => s.showWaveforms);
  const { sourceDuration, sourceStart } = useMediaSourceTiming(item.mediaId, item, fps);
  const trimStart = (item.trimStart ?? 0) / fps;
  const speed = item.speed ?? 1;

  return (
    <div className="absolute inset-0 flex flex-col">
      <div
        className="px-2 text-[11px] font-medium truncate shrink-0"
        style={{ height: CLIP_LABEL_ROW_HEIGHT, lineHeight: `${CLIP_LABEL_ROW_HEIGHT}px` }}
      >
        {item.label}
      </div>
      {showWaveforms && item.mediaId && (
        <div className="relative overflow-hidden bg-waveform-gradient flex-1 min-h-0">
          <ClipWaveform
            mediaId={item.mediaId}
            clipWidth={clipWidth}
            sourceStart={sourceStart}
            sourceDuration={sourceDuration}
            trimStart={trimStart}
            speed={speed}
            fps={fps}
            isVisible={isClipVisible}
            pixelsPerSecond={pixelsPerSecond}
          />
        </div>
      )}
    </div>
  );
}

function CompositionClipContent({
  item,
  clipWidth,
  fps,
  isClipVisible,
  pixelsPerSecond,
  preferImmediateRendering = false,
}: ClipContentProps) {
  const showWaveforms = useSettingsStore((s) => s.showWaveforms);
  const showFilmstrips = useSettingsStore((s) => s.showFilmstrips);
  const compositionId = item.type === 'composition' ? item.compositionId : undefined;
  const compTopVideoMediaId = useCompositionsStore(
    useCallback((s) => {
      if (!compositionId) return null;
      const comp = s.compositions.find((c) => c.id === compositionId);
      if (!comp) return null;
      const trackOrderMap = new Map(comp.tracks.map((t) => [t.id, t.order ?? 0]));
      let topMediaId: string | null = null;
      let topOrder = Infinity;
      for (const ci of comp.items) {
        if (ci.type !== 'video' || !ci.mediaId) continue;
        const order = trackOrderMap.get(ci.trackId) ?? 0;
        if (order < topOrder) {
          topOrder = order;
          topMediaId = ci.mediaId;
        }
      }
      return topMediaId;
    }, [compositionId])
  );
  const { sourceDuration, sourceStart } = useMediaSourceTiming(compTopVideoMediaId, item, fps);

  if (!compTopVideoMediaId) {
    return (
      <div className="absolute inset-0 flex flex-col px-2 py-1 overflow-hidden">
        <div className="text-[10px] text-muted-foreground truncate">Pre-Comp</div>
        <div className="text-xs font-medium truncate flex-1">
          {item.label || 'Composition'}
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 flex flex-col">
      <div
        className="px-2 text-[11px] font-medium truncate shrink-0"
        style={{ height: CLIP_LABEL_ROW_HEIGHT, lineHeight: `${CLIP_LABEL_ROW_HEIGHT}px` }}
      >
        {item.label || 'Composition'}
      </div>
      <div className="relative overflow-hidden flex-1 min-h-0">
        {showFilmstrips && (
          <ClipFilmstrip
            mediaId={compTopVideoMediaId}
            clipWidth={clipWidth}
            sourceStart={sourceStart}
            sourceDuration={sourceDuration}
            trimStart={0}
            speed={1}
            fps={fps}
            isVisible={isClipVisible}
            pixelsPerSecond={pixelsPerSecond}
            preferImmediateRendering={preferImmediateRendering}
          />
        )}
      </div>
      {showWaveforms && (
        <div className="relative overflow-hidden bg-waveform-gradient" style={{ height: VIDEO_WAVEFORM_HEIGHT }}>
          <ClipWaveform
            mediaId={compTopVideoMediaId}
            clipWidth={clipWidth}
            sourceStart={sourceStart}
            sourceDuration={sourceDuration}
            trimStart={0}
            speed={1}
            fps={fps}
            isVisible={isClipVisible}
            pixelsPerSecond={pixelsPerSecond}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Renders the visual content of a timeline clip based on its type.
 * Video/audio/composition items keep their richer preview path, while simple
 * items avoid subscribing to unrelated stores to keep dense timelines smoother.
 */
export const ClipContent = memo(function ClipContent(props: ClipContentProps) {
  const { item } = props;

  if (item.type === 'video' && item.mediaId) {
    return <VideoClipContent {...props} />;
  }

  if (item.type === 'audio' && item.mediaId) {
    return <AudioClipContent {...props} />;
  }

  if (item.type === 'composition') {
    return <CompositionClipContent {...props} />;
  }

  return <SimpleClipContent item={item} />;
});
